import { put, select, takeLatest } from 'redux-saga/effects'

import { getPayrollPeriodFromDate } from '../../@paco/entities/PayrollPeriod/PayrollPeriodHelpers'
import { transformLegacyPayrollPeriodToPayrollPeriod } from '../../@paco/entities/PayrollPeriod/PayrollPeriodTransformers'
import { getMiddleOfDateRange, getMiddleOfDays, isDateInRange } from '../../@paco/helpers/date'
import {
    calendarSlice,
    setActivePayrollPeriod,
    setSelectedDays,
    setSelectedMobileDay,
    setTimeMode,
} from '../../@paco/redux/@interface/calendar/calendarReducer'

import { TIME_MODES } from '../../constants'
import {
    addDays,
    eachDayOfInterval,
    getRangeFromDate,
    getStartOfWorkDay,
} from '../../helpers/date'
import {
    WEEK_NAVIGATOR_SAGA_SET_DATES,
    WEEK_NAVIGATOR_SAGA_SET_SELECTED_DATE,
    WEEK_NAVIGATOR_SAGA_SET_TIME_MODE,
    WEEK_NAVIGATOR_SET_ACTIVE_PAYROLL_PERIOD,
    WEEK_NAVIGATOR_SET_DATES,
    WEEK_NAVIGATOR_SET_SELECTED_DATE,
    WEEK_NAVIGATOR_SET_TIME_MODE,
} from '../actionTypes'

function* setDates(action) {
    const { selectedDate, startDate, endDate } = action
    const state = yield select()
    const { payrollPeriods } = state.appReducer

    const selectedDays = eachDayOfInterval(startDate, endDate)
    const payrollPeriod = getPayrollPeriodFromDate(
        getMiddleOfDays(selectedDays),
        payrollPeriods.map(transformLegacyPayrollPeriodToPayrollPeriod),
    )

    yield put({ type: WEEK_NAVIGATOR_SET_DATES, startDate, endDate })
    yield put({ type: WEEK_NAVIGATOR_SET_SELECTED_DATE, selectedDate: selectedDate || startDate })
    yield put({ type: WEEK_NAVIGATOR_SET_ACTIVE_PAYROLL_PERIOD, payrollPeriod })

    // Trigger @paco/calendar reducer actions
    yield put(setSelectedDays(selectedDays))
    yield put(setSelectedMobileDay(selectedDate || startDate))
    yield put(setActivePayrollPeriod(payrollPeriod))
}

function* setSelectedDate(action) {
    const { selectedDate, startDate, endDate } = action
    const state = yield select()
    const { payrollPeriods } = state.appReducer
    const { mode } = state.weekNavigatorReducer

    yield put({ type: WEEK_NAVIGATOR_SET_SELECTED_DATE, selectedDate })
    yield put(setSelectedMobileDay(selectedDate))

    if (! isDateInRange(selectedDate, { start: startDate, end: endDate })) {
        const newRange = getRangeFromDate(selectedDate, mode, payrollPeriods);
        const newStartDate = newRange[0]
        const newEndDate = newRange[newRange.length - 1]
        const payrollPeriod = getPayrollPeriodFromDate(
            getMiddleOfDateRange(newStartDate, newEndDate),
            payrollPeriods.map(transformLegacyPayrollPeriodToPayrollPeriod),
        )

        yield put({ type: WEEK_NAVIGATOR_SET_DATES, startDate: newStartDate, endDate: newEndDate })
        yield put({ type: WEEK_NAVIGATOR_SET_ACTIVE_PAYROLL_PERIOD, payrollPeriod })

        // Trigger @paco/calendar reducer actions
        yield put(setSelectedDays(newRange))
    }
}

function* setLegacyTimeMode(action) {
    const state = yield select()
    const { payrollPeriods } = state.appReducer
    const { selectedDateAtTimeSwitch, selectedDate } = state.weekNavigatorReducer
    const { mode } = state.weekNavigatorReducer

    // When switching back to week from month we want to go back to the date selected
    // in week view
    let newSelectedDate = selectedDateAtTimeSwitch || selectedDate

    // Adding a few days to prevent jumping to previous month / year
    if (mode === TIME_MODES.WEEK) {
        newSelectedDate = addDays(selectedDate, 3.5)
    } else if (((mode === TIME_MODES.PERIOD) && (action.mode === TIME_MODES.MONTH))
        || ((action.mode === TIME_MODES.YEAR))) {
        newSelectedDate = addDays(selectedDate, 14)
    }

    const dates = getRangeFromDate(newSelectedDate, action.mode, payrollPeriods)
    const payrollPeriod = getPayrollPeriodFromDate(getMiddleOfDays(dates), payrollPeriods.map(transformLegacyPayrollPeriodToPayrollPeriod))

    yield put({ type: WEEK_NAVIGATOR_SET_DATES, startDate: dates[0], endDate: dates[dates.length - 1] })
    yield put({ type: WEEK_NAVIGATOR_SET_ACTIVE_PAYROLL_PERIOD, payrollPeriod })
    yield put({ type: WEEK_NAVIGATOR_SET_SELECTED_DATE, selectedDate: dates[0] })
    yield put({
        type: WEEK_NAVIGATOR_SET_TIME_MODE,
        mode: action.mode,
        prevSelectedDate: selectedDate,
    })

    // Trigger @paco/calendar reducer actions
    yield put(setSelectedDays(dates))
    yield put(setTimeMode(action.mode))
    yield put(setActivePayrollPeriod(payrollPeriod))
    yield put(setSelectedMobileDay(selectedDate))
}

function* onSetSelectedDays(action) {
    const { payload: days } = action

    const startDate = getStartOfWorkDay(days[0])
    const endDate = getStartOfWorkDay(days[days.length - 1])

    yield put({
        type: WEEK_NAVIGATOR_SET_DATES,
        startDate,
        endDate,
    })
}

function* onSetActivePayrollPeriod(action) {
    const { payload: payrollPeriod } = action

    yield put({
        type: WEEK_NAVIGATOR_SET_ACTIVE_PAYROLL_PERIOD,
        payrollPeriod,
    })
}

function* onSetSelectedMobileDay(action) {
    const { payload: selectedDate } = action

    yield put({ type: WEEK_NAVIGATOR_SET_SELECTED_DATE, selectedDate: getStartOfWorkDay(selectedDate) })
}

function* onSetTimeMode(action) {
    const { payload: timeMode } = action
    const state = yield select()
    const { selectedDateAtTimeSwitch, selectedDate } = state.weekNavigatorReducer

    yield put({
        type: WEEK_NAVIGATOR_SET_TIME_MODE,
        prevSelectedDate: selectedDateAtTimeSwitch || selectedDate,
        mode: timeMode,
    })
}

export default function* weekNavigatorWatcher() {
    yield takeLatest(WEEK_NAVIGATOR_SAGA_SET_DATES, setDates)
    yield takeLatest(WEEK_NAVIGATOR_SAGA_SET_SELECTED_DATE, setSelectedDate)
    yield takeLatest(WEEK_NAVIGATOR_SAGA_SET_TIME_MODE, setLegacyTimeMode)

    // Listen for changes in @paco/calendar store
    yield takeLatest(calendarSlice.actions.setSelectedDays, onSetSelectedDays)
    yield takeLatest(calendarSlice.actions.setActivePayrollPeriod, onSetActivePayrollPeriod)
    yield takeLatest(calendarSlice.actions.setTimeMode, onSetTimeMode)
    yield takeLatest(calendarSlice.actions.setSelectedMobileDay, onSetSelectedMobileDay)
}
